/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useRef, useEffect, useMemo } from "react";

import moment from "moment-timezone";

// @mui material components
import Grid from "@mui/material/Grid";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";

import { Bar, Chart } from "react-chartjs-2";

//Backend access
import { loadToken, getCameraHistory } from 'api/bks';
import PropTypes from "prop-types";

import { useAuth0, withAuthenticationRequired } from "@auth0/auth0-react";
import CameraImage from "nextus/CameraImage";
import { PacmanLoader } from "react-spinners";
import { Chart as Chartjs, registerables } from 'chart.js';
import zoomPlugin from "chartjs-plugin-zoom";
Chartjs.register(...registerables);
Chartjs.register(zoomPlugin); // REGISTER PLUGIN

var timeout, current_chart;

// Images
function CameraFeedChart({ uuid, startTime, endTime, setStartTime, setEndTime, chartData, isLoading, setIsLoading, current_camera_url }) {
  const [selectedImage, setSelectedImage] = useState({
    data: chartData[Number(chartData.length) - 1] ? chartData[Number(chartData.length) - 1] : null,
    index: chartData[Number(chartData.length) - 1] ? Number(chartData.length) - 1 : -1
  });

  const chartRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(null);

  loadToken(useAuth0());

  function nextImage() {
    var new_image = {
      index: selectedImage.index,
      data: selectedImage.data
    };

    new_image.index++;

    if (new_image.index == -1) {
      new_image.index = 0;
    }
    else if (chartData && new_image.index >= chartData.length) {
      new_image.index = chartData.length - 1;
    }

    if (typeof chartData != 'undefined' && typeof chartData[new_image.index] != 'undefined') {
      new_image.data = chartData[new_image.index];
    }

    setSelectedImage(new_image);
  }

  function previousImage() {
    var new_image = {
      index: selectedImage.index,
      data: selectedImage.data
    };

    new_image.index--;

    if (new_image.index == -1) {
      new_image.index = 0;
    }
    else if (new_image.index >= chartData.length) {
      new_image.index = chartData.length - 1;
    }

    if (typeof chartData != 'undefined' && typeof chartData[new_image.index] != 'undefined') {
      new_image.data = chartData[new_image.index];
    }

    setSelectedImage(new_image);

  }

  function handleKeyDown(e) {
    // arrow up/down button should select next/previous list element
    if (e.keyCode === 37) {
      previousImage();
    } else if (e.keyCode === 39) {
      nextImage();
    }
  }

  // console.log(data)
  const scrollEvent = (chart) => {
    // console.log(timeout);
    current_chart = chart;
    if (typeof timeout != 'undefined') {
      clearTimeout(timeout);
      setIsLoading(false);
      timeout = undefined;
    }

    timeout = setTimeout(() => {
      runWheelScrollEvent(current_chart);
      setIsLoading(true);
      timeout = undefined;
    }, 400);
  }

  const runWheelScrollEvent = ({ chart }) => {
    var start_time, end_time;
    let min_limit = endTime.clone().subtract(7, 'day');
    let max_limit = startTime.clone().add(7, 'day');
    // console.log(chart.scales.x.min)
    let min_moment = moment(chart.scales.x.min);
    let max_moment = moment(chart.scales.x.max);

    if (min_moment.isBefore(min_limit)) {
      start_time = min_limit;
    }
    else {
      start_time = min_moment;
    }

    if (max_moment.isAfter(max_limit)) {
      end_time = max_limit;
    }
    else {
      end_time = max_moment;
    }

    setChartWidth(chart.width);
    setStartTime(start_time);
    setEndTime(end_time);

  };

  const options = {
    maintainAspectRatio: false,
    responsive: true,
    animation: {
      duration: 0
    },
    scales: {
      x: {
        type: 'time',
        min: startTime.unix() * 1000,
        max: endTime.unix() * 1000,
      },
      y: {
        ticks: {
          // Include a dollar sign in the ticks
          callback: function (value, index, ticks) {
            if (Math.floor(value) === value) {
              if (value == -1) {
                return "IMAGE"
              }
              return value;
            }
          }
        },
        min: -1
      }
    },
    fill: true,
    borderJoinStyle: "bevel",
    // borderColor: "darkred",
    tension: 0.2,
    grouped: true,
    plugins: {
      zoom: {
        zoom: {
          mode: 'x',
          drag: {
            enabled: false,
          },
          wheel: {
            enabled: true,
          },
          pinch: {
            enabled: false,
          },
          speed: 0.5, sensitivity: 0.5,
          onZoomComplete: scrollEvent,
        },
        limits: {
          x: {
            min: 0,
            max: moment().unix() * 1000,
            minRange: 5000 * 1000,
          }
        }
      },
      tooltip: {
        events: ["click"],
        callbacks: {
          label: function (context) {

            // debugger;
            if (context.raw && context.raw.data) {

              if (!selectedImage.data || context.raw.data._id != selectedImage.data._id) {
                // console.log(context)
                setSelectedImage({
                  index: context.dataIndex,
                  data: context.raw.data
                });
              }
            }

          },
        }
      },
      legend: {
        position: 'top',
        display: false
      }
    }
  }
  var chart_data = {
    datasets: []
  }

  if (chartWidth || chartRef.current) {
    var width = chartWidth ? chartWidth : chartRef.current.width;
    var barThickness = (Number(width) / Number(chartData.length));
    if(barThickness > 100){
      barThickness = 30;
    }
    if (barThickness > 10) {
      barThickness -= 4;
    }
    else {
      barThickness -= 1;
    }
  }
  else {
    var barThickness = 40;
  }

  chart_data.datasets.push({
    label: uuid,
    backgroundColor: function (color) {
      try {
        if (selectedImage.data && selectedImage.data._id == color.raw.data._id) {
          return "blue";
        }
        else {
          return "lightgrey"
        }
      }
      catch (e) {
        return "lightgrey"
      }
    },
    data: chartData.map(function (row) {
      return { 'x': row.timestamp * 1000, 'y': [-1, row.objects.length || 0], 'data': row }
    }),
    barThickness: barThickness
  })

  const chart = (<Bar
    ref={chartRef}
    data={chart_data}
    options={options}
  />);

  const loading = (<>
    <Grid item xxs={12} sm={12} md={12} lg={12} xl={12} >
      <SoftBox sx={{ height: "50vh", width: "100%" }}>
        <SoftBox sx={{ height: "100%" }}>
          <Grid container sx={{ height: "100%" }}>
            <Grid item xxs={12} sm={12} md={12} lg={12} xl={12} sx={{ height: "100%" }}>
              <SoftBox sx={{ 'object-fit': "contain", textAlign: 'center', height: "100%", width: "100%" }} >
                <SoftBox
                  component="img"
                  src={current_camera_url}
                  position="relative"
                  maxWidth="100%"
                  height="100%"
                  borderRadius="lg"
                />
              </SoftBox>
            </Grid>
          </Grid>
        </SoftBox>
      </SoftBox>
    </Grid>
    <Grid item xxs={12} sm={12} md={12} lg={12} xl={12}>
      <SoftBox sx={{ opacity: '0.5', height: "20vh", width: "100%" }}>
        {chart}
      </SoftBox>
    </Grid>
  </>);

  const rendered = (<>
    <Grid item xxs={12} sm={12} md={12} lg={12} xl={12} >
      <SoftBox sx={{ height: "50vh", width: "100%" }}>
        {selectedImage.data && <CameraImage image_data={selectedImage.data} />}
      </SoftBox>
    </Grid>
    <Grid item xxs={12} sm={12} md={12} lg={12} xl={12}>
      <SoftBox sx={{ height: "20vh", width: "100%" }}>
        {chart}
      </SoftBox>
    </Grid>
  </>);

  return (
    <SoftBox p={2}>
      <Grid container onKeyDown={handleKeyDown} tabIndex={0}>
        {isLoading && loading}
        {!isLoading && rendered}
      </Grid>
    </SoftBox>

  );
}

// Typechecking props for the CameraFeed
CameraFeedChart.propTypes = {
  uuid: PropTypes.string,
  startTime: PropTypes.object,
  setStartTime: PropTypes.func,
  endTime: PropTypes.object,
  setEndTime: PropTypes.func,
  chartData: PropTypes.object,
  isLoading: PropTypes.bool,
  setIsLoading: PropTypes.func,
  current_camera_url: PropTypes.string
};


export default CameraFeedChart;
